import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { logLoginSession, registerDevice } from "@/lib/deviceInfo";
import { useProfile, resetProfileFetchFlag } from "./useProfile";
import { useSubscription } from "./useSubscription";

// Re-export for backwards compatibility
export type { Profile } from "./useProfile";
export type { Subscription } from "./useSubscription";
export { useSubscription } from "./useSubscription";
export { useProfile } from "./useProfile";

// Internal hook — runs the real auth logic. ONLY call from AuthProvider.
// All component-level usage goes through the Context-backed useAuth() below,
// so we avoid creating one auth instance (with its own fetchSubscription +
// heartbeat PATCH + realtime channel) per component that imports useAuth.
const useAuthImpl = () => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const isMountedRef = useRef(true);

  const {
    profile,
    fetchProfile,
    clearProfile,
    getFullName,
    isMountedRef: profileMountedRef,
  } = useProfile();

  const {
    subscription,
    isOrgSeat,
    fetchSubscription,
    clearSubscription,
    getTrialDaysRemaining,
    isTrialExpired,
    isLite,
    isStandard,
    isPremium,
    isPro,
    isEnterprise,
    isPaid,
    isTrial,
    getProjectLimit,
    getOfferLimit,
    canGenerateOffers,
    getPlanName,
  } = useSubscription({ user, session, loading });

  const clearInvalidSession = async () => {
    setUser(null);
    setSession(null);
    clearProfile();
    clearSubscription();

    // Clear all Supabase auth data from localStorage. Wrap everything in
    // a single try — Safari private mode + Firefox content blocking can
    // throw SecurityError on both Object.keys(localStorage) AND each
    // removeItem call. We don't want clearInvalidSession itself to throw
    // and short-circuit the supabase.auth.signOut() below.
    try {
      const keysToRemove = [
        'sb-placeholder-auth-token',
        'supabase.auth.token',
        'supabase.auth.session',
      ];

      keysToRemove.forEach(key => {
        try { localStorage.removeItem(key); } catch { /* private mode */ }
      });

      // Also clear any Supabase-namespaced localStorage items
      Object.keys(localStorage).forEach(key => {
        if (key.includes('supabase') || key.includes('sb-')) {
          try { localStorage.removeItem(key); } catch { /* private mode */ }
        }
      });
    } catch {
      // Object.keys(localStorage) itself can throw — fall through.
    }

    await supabase.auth.signOut();
  };

  const isRefreshTokenError = (err: any): boolean => {
    if (!err) return false;
    const errorMessage = err?.message || '';
    return (
      errorMessage.includes('Invalid Refresh Token') ||
      errorMessage.includes('Refresh Token Not Found') ||
      errorMessage.includes('refresh token') ||
      err?.code === 'invalid_grant' ||
      err?.code === 'invalid_token'
    );
  };

  useEffect(() => {
    isMountedRef.current = true;
    profileMountedRef.current = true;
    let authTimeoutId: ReturnType<typeof setTimeout> | null = null;

    const initAuth = async () => {
      try {
        // Create a timeout to prevent hanging in sandbox environments
        authTimeoutId = setTimeout(() => {
          if (isMountedRef.current) {
            console.warn('Auth initialization timeout - allowing app to load anyway');
            setLoading(false);
          }
        }, 5000);

        // Get current session
        const {
          data: { session: currentSession },
          error: sessionError,
        } = await supabase.auth.getSession();

        if (!isMountedRef.current) return;
        if (authTimeoutId) clearTimeout(authTimeoutId);

        if (sessionError) {
          if (isRefreshTokenError(sessionError)) {
            await clearInvalidSession();
            if (isMountedRef.current) setLoading(false);
            return;
          }

          throw sessionError;
        }

        if (isMountedRef.current) {
          // Check if MFA is required but not yet verified.
          // EXCEPTION: skip MFA enforcement during password recovery flow —
          // the recovery session is single-purpose (update password) and
          // the MFA gate would destroy it before the user can submit, which
          // surfaced as "session invalid" after entering the new password.
          // We re-authenticate (incl. MFA) on next login after the reset.
          //
          // SCOPE: only honour the recovery exception on /auth itself. A
          // previous version used a substring match against any URL, which
          // let an attacker with just the password append
          // `?mode=update-password` to any in-app URL and skip the MFA
          // gate at aal1. The recovery UI is only rendered at /auth.
          const isPasswordRecovery =
            typeof window !== 'undefined' &&
            window.location.pathname === '/auth' &&
            (window.location.search.includes('mode=update-password') ||
             window.location.hash.includes('type=recovery'));

          if (currentSession?.user && !isPasswordRecovery) {
            const { data: aalData } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
            if (aalData?.currentLevel === 'aal1' && aalData?.nextLevel === 'aal2') {
              // User has MFA enabled but hasn't completed verification — sign them out
              await supabase.auth.signOut();
              setSession(null);
              setUser(null);
              setLoading(false);
              return;
            }
          }

          setSession(currentSession);
          setUser(currentSession?.user ?? null);

          if (currentSession?.user) {
            // Fetch profile and subscription in parallel, then set loading to false
            // This prevents a flash where subscription is null
            Promise.allSettled([
              fetchProfile(currentSession.user.id),
              fetchSubscription(currentSession.user.id),
            ]).then(results => {
              if (results[0].status === 'rejected') {
                console.error('Failed to fetch profile:', results[0].reason);
              }
              if (results[1].status === 'rejected') {
                console.error('Failed to fetch subscription:', results[1].reason);
              }
              if (isMountedRef.current) {
                setLoading(false);
              }
            }).catch(err => {
              console.error('Background fetch error:', err);
              if (isMountedRef.current) {
                setLoading(false);
              }
            });
          } else {
            setLoading(false);
          }
        }
      } catch (err) {
        // If it's a refresh token error, clear the session
        if (isRefreshTokenError(err)) {
          await clearInvalidSession();
        } else {
          console.debug('Auth initialization error:', err);
        }
        if (isMountedRef.current) {
          setLoading(false);
        }
      }
    };

    initAuth();

    // Subscribe to auth changes
    const {
      data: { subscription: authSubscription },
    } = supabase.auth.onAuthStateChange(async (event, newSession) => {
      console.debug('Auth state changed:', event);
      try {
        if (event === 'SIGNED_OUT') {
          // Clear user-specific localStorage items to prevent stale data
          // leaking to the next user on a shared computer. Includes any
          // key that holds buyer / quote / VAT identity, project payloads,
          // or per-user feature state — even if it has its own cleanup
          // path elsewhere, doing it here is defense in depth.
          const userScopedKeys = [
            'pendingUpgrade',
            'onboarding_v2_done',
            'pending_signup_redirect',
            'todaidesign_offer_templates', // legacy migration cleanup
            // Quote/offer state — would otherwise pre-fill the next user's
            // first quote with the prior user's margins, VAT IDs, contact.
            'todaidesign_quote_config',
            'dxf-export-prefill',
            'cctv_plan_for_offer', // base64 PDF image of last designer canvas
          ];
          // Deliberately NOT cleared on logout:
          //   - todaidesign_api_keys: user-issued PUBLIC API keys (cpk_…)
          //     used by their Zapier/CRM. Wiping on signout would silently
          //     break those integrations on the device.
          //   - lead_email_captured: per-browser anti-nag state, not
          //     per-user identity. Wiping it re-shows the popup the user
          //     already dismissed.
          for (const k of userScopedKeys) {
            try { localStorage.removeItem(k); } catch { /* ignore quota / privacy errors */ }
          }
        }

        if (isMountedRef.current) {
          setSession(newSession);
          setUser(newSession?.user ?? null);
        }

        if (newSession?.user) {
          // Log device info on sign-in (fire-and-forget)
          if (event === 'SIGNED_IN') {
            resetProfileFetchFlag(); // Reset on new sign-in
            logLoginSession(newSession.user.id);
          }
          // Device-limit registry (monitor mode — never blocks). Also on
          // INITIAL_SESSION so already-logged-in devices get recorded, not just
          // fresh sign-ins. register_device dedups known devices, so it's cheap.
          if (event === 'SIGNED_IN' || event === 'INITIAL_SESSION') {
            registerDevice(newSession.user.id);
          }

          // Only fetch profile/subscription on sign-in and user update
          // TOKEN_REFRESHED happens every hour and doesn't need re-fetches
          if (event === 'SIGNED_IN' || event === 'USER_UPDATED') {
            // Fetch profile and subscription, then set loading to false
            // This prevents a flash where subscription is null
            Promise.allSettled([
              fetchProfile(newSession.user.id),
              fetchSubscription(newSession.user.id),
            ]).then(results => {
              if (results[0].status === 'rejected') {
                console.error('Failed to fetch profile:', results[0].reason);
              }
              if (results[1].status === 'rejected') {
                console.error('Failed to fetch subscription:', results[1].reason);
              }
              if (isMountedRef.current) {
                setLoading(false);
              }
            }).catch(err => {
              console.error('Background fetch error:', err);
              if (isMountedRef.current) {
                setLoading(false);
              }
            });
          } else if (event === 'INITIAL_SESSION') {
            // initAuth() handles initial fetch+loading. Don't prematurely flip loading=false
            // here (race caused Free-plan banner to flash until subscription fetch resolved).
          } else {
            // TOKEN_REFRESHED — no need to re-fetch, just ensure loading is false
            if (isMountedRef.current) {
              setLoading(false);
            }
          }
        } else if (isMountedRef.current) {
          clearProfile();
          clearSubscription();
          setLoading(false);
        }
      } catch (err) {
        console.error('Auth state change error:', err);
        if (isRefreshTokenError(err)) {
          await clearInvalidSession();
        }
        if (isMountedRef.current) {
          setLoading(false);
        }
      }
    });

    return () => {
      isMountedRef.current = false;
      profileMountedRef.current = false;
      if (authTimeoutId) clearTimeout(authTimeoutId);
      authSubscription?.unsubscribe();
    };
  }, [fetchProfile, fetchSubscription]);

  const signOut = async () => {
    try {
      resetProfileFetchFlag(); // Reset so next login can fetch profile
      await supabase.auth.signOut();
      setUser(null);
      setSession(null);
      clearProfile();
      clearSubscription();
    } catch (err) {
      console.error("Error signing out:", err);
    }
  };

  return {
    user,
    session,
    profile,
    subscription,
    isOrgSeat,
    loading,
    signOut,
    // Re-read the subscription row from the server. Needed after a Paystack
    // redirect confirms a charge: that write lands via paystack-verify, not
    // through this hook, and `subscriptions` isn't in the realtime
    // publication, so nothing pushes the change to already-mounted state.
    fetchSubscription,
    getFullName,
    getTrialDaysRemaining,
    isTrialExpired,
    isPaid,
    isTrial,
    // Read by the synced designer (upstream names). On our ladder a trial
    // is the free plan, and anything unpaid exports with the watermark.
    subscriptionLoading: false,
    isTrialing: () => isTrial(),
    shouldWatermark: () => !isPaid(),
    isLite,
    isStandard,
    isPremium,
    isPro,
    isEnterprise,
    getProjectLimit,
    getOfferLimit,
    canGenerateOffers,
    getPlanName,
    resendVerificationEmail: async () => {
      if (!user?.email) return false;
      const { error } = await supabase.auth.resend({ type: 'signup', email: user.email });
      return !error;
    },
    // Force-refresh user from the server. Needed after email confirmation
    // in a different tab — getSession() returns a stale cached user with
    // email_confirmed_at=null even though the server has already flipped it.
    refreshUser: async () => {
      const { data, error } = await supabase.auth.getUser();
      if (error || !data.user) return false;
      if (isMountedRef.current) setUser(data.user);
      return true;
    },
  };
};

// ============================================================================
// React Context — single source of auth state for the whole tree.
//
// Before: each useAuth() call created a fresh instance → each fired its own
// fetchSubscription GET + heartbeat PATCH + realtime channel. With ~20 auth-
// aware components on dashboard, that produced ~20x the API traffic and a
// visible 2-minute "Free plan" flicker while fetches queued behind each other.
// ============================================================================

type AuthContextValue = ReturnType<typeof useAuthImpl>;
const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const value = useAuthImpl();
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// SSG fallback — prerender happens without a real AuthProvider; return a
// logged-out state so pages render without crashing.
const ssgFallback: AuthContextValue = {
  user: null,
  session: null,
  profile: null,
  subscription: null,
  isOrgSeat: false,
  loading: false,
  signOut: async () => {},
  fetchSubscription: async () => {},
  getFullName: () => '',
  getTrialDaysRemaining: () => 0,
  isTrialExpired: () => false,
  isPaid: () => false,
  isTrial: () => true,
  subscriptionLoading: false,
  isTrialing: () => true,
  shouldWatermark: () => true,
  isLite: () => false,
  isStandard: () => false,
  isPremium: () => false,
  isPro: () => false,
  isEnterprise: () => false,
  getProjectLimit: () => 1,
  getOfferLimit: () => 1,
  canGenerateOffers: () => true,
  getPlanName: () => 'Free',
  resendVerificationEmail: async () => false,
  refreshUser: async () => false,
};

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  return ctx ?? ssgFallback;
};
